import { appConfigStore } from 'Models/AppConfigStore';
import React from 'react';
import { formatStandardDate } from 'Util/_HumanWritten/DateUtils';
import { Button } from 'Views/Components/Button/Button';
import If from 'Views/Components/If/If';
import { DateRangePicker } from 'Views/Components/DateRangePicker/DateRangePicker';
import { DisplayType } from 'Views/Components/Models/Enums';
import { store } from 'Models/Store';
import classNames from 'classnames';

export interface DateRangeFilterProps {
	id?: string;
	className?: string;
	startDate?: Date;
	endDate?: Date;
	onChangeDate: (dates: { startDate: Date; endDate: Date }) => void;
	onClear: () => void;
	minDate?: string;
	maxDate?: string;
	options: string[];
	active: string;
	onChangeFilterType?: (selected: string) => void;
	clearable?: boolean;
}

function FhDateRangeFilter({
	id = 'date-range-filter',
	className = '',
	startDate,
	endDate,
	onChangeDate,
	onClear,
	minDate = undefined,
	maxDate = undefined,
	options,
	active,
	onChangeFilterType,
	clearable = true,
}: DateRangeFilterProps) {
	// `range` is what the picker writes to; only `startDate`/`endDate` are read back (below and
	// in the render function), the selection itself is handled by `handleDateChange`.
	const model: { startDate: Date; endDate: Date; range?: Date[] } = {
		startDate: startDate ?? new Date(),
		endDate: endDate ?? new Date(),
	};

	const datesSelected = !!startDate && !!endDate;

	const handleDateChange = (dates: Date[]) => {
		const [start, end] = dates;
		if (start && end) {
			onChangeDate({ startDate: start, endDate: end });
		}
	};

	return (
		<DateRangePicker
			id={id}
			model={model}
			modelProperty="range"
			className={classNames('fh-date-range-filter', className)}
			displayType={DisplayType.BLOCK}
			minDate={minDate}
			maxDate={maxDate}
			flatpickrOptions={{
				minDate: store.isStaff ? '' : minDate,
				monthSelectorType: 'static',
				allowInput: false,
				position: 'below left',
				locale: {
					firstDayOfWeek: appConfigStore.CalendarStartDay,
					weekdays: {
						shorthand: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
						longhand: ['Sun', 'Mon', 'Tue', 'Wed', 'Thur', 'Fri', 'Sat'],
					},
				},
			}}
			flatpickrProps={{
				value: datesSelected ? [model.startDate, model.endDate] : undefined,
				onChange: handleDateChange,
				render: (props, ref) => (
					<div className={classNames('fh-filter-button', { singular: options.length <= 1 })}>
						<div className="fh-date-filter">
							<If condition={options.length > 1}>
								<div className="fh-date-filter__options">
									{options.map(option => (
										<button
											key={option}
											onClick={() => {
												if (onChangeFilterType) {
													onChangeFilterType(option);
												}
											}}
											className={classNames('fh-toggle__button', {
												'fh-toggle__button--active': active === option,
											})}
										>
											{option}
										</button>
									))}
								</div>
							</If>
							<div
								ref={ref}
								className={classNames('fh-date-filter__text ', {
									'fh-popover__trigger': options.length <= 1,
								})}
							>
								<If condition={options.length <= 1}>
									<p className="grey">{options[0]}</p>
								</If>
								<p>
									{datesSelected
										? `${formatStandardDate(model.startDate)} - ${formatStandardDate(model.endDate)}`
										: 'All dates'}
								</p>
							</div>
							<If condition={clearable && datesSelected}>
								<Button
									className="staff-bookings__clear-dates"
									icon={{ icon: 'cross', iconPos: 'icon-right' }}
									onClick={onClear}
								/>
							</If>
						</div>
					</div>
				),
			}}
		/>
	);
}

export default FhDateRangeFilter;
